/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import img from "../../Assets/is.png";
import getTranslationData from "../../TranslationUtility/TranslationUtility";
import { getMinPrice } from "../../Common/HOC/Hoc";
import {
  calculateEarning,
  getValidDenominations,
  useEarnRoiCalculationContext,
  usePoints,
} from "../Utility/Utility";
import { getRewardRoundingSettings } from "./utils/rewardRounding";

const ThePoints = ({
  productReducer,
  points,
  setPoints,
  updatedData,
  productData,
  onTotalPayableAmountChange,
  setBurnPoints,
  cash,
  setCash,
  minDenomination,
  productDataw,
  stockOutDenominations,
  selectedCardBasedRedemtion,
  matchedCardMasterData,
  journeyType,
  onValidationStateChange,
}) => {
  const currentBalance = usePoints();
  const rewardRoiCalculationContext = useEarnRoiCalculationContext({
    selectedLmsCard: selectedCardBasedRedemtion,
    cardMasterConfiguration: matchedCardMasterData,
  });

  const selectedPriceDenomination =
    productDataw?.productReducerDisocount?.clientProductDenomination?.map(
      (deno) => deno.denomination,
    ) || [];

  const translation = getTranslationData();
  let totalPayableAmount = 0;
  if (!productReducer?.modalProductData) {
    updatedData?.forEach((item) => {
      const itemCost = item.denomination * item.quantity;
      const discountAmount = (itemCost * (productData?.customerDiscount || 0)) / 100;
      const costAfterDiscount = itemCost - discountAmount;
      const convenienceFee = (costAfterDiscount * (productData?.convenienceFee || 0)) / 100;
      const gstAmount = (convenienceFee * (productData?.gst || 0)) / 100;
      totalPayableAmount += costAfterDiscount + convenienceFee + gstAmount;
    });
  } else {
    productReducer?.modalProductData?.forEach((item) => {
      const itemCost = item.denomination * item.quantity;
      const discountAmount = (itemCost * (productData?.customerDiscount || 0)) / 100;
      const costAfterDiscount = itemCost - discountAmount;
      const convenienceFee = (costAfterDiscount * (productData?.convenienceFee || 0)) / 100;
      const gstAmount = (convenienceFee * (productData?.gst || 0)) / 100;
      totalPayableAmount += costAfterDiscount + convenienceFee + gstAmount;
    });
  }
  const burnBaseDenomination = getMinPrice(selectedPriceDenomination);
  const roundingSettings = getRewardRoundingSettings(
    rewardRoiCalculationContext.globalConfiguration?.pointRoundingConfiguration,
  );
  const burnCalculation = calculateEarning({
    ...rewardRoiCalculationContext,
    amount: totalPayableAmount,
    minBurnPoints: productData?.maxEarnPoints,
    baseDenomination: burnBaseDenomination,
    burnRoundMethod: roundingSettings?.roundMethod ?? 5,
    burnRoundDigits: roundingSettings?.roundDigits ?? 0,
  });
  useEffect(() => {
    if (points > 0 && currentBalance < points) {
      applyPoints(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBalance]);

  const maxBurnPoints = () => {
    return burnCalculation.pointsRequired;
  };

  const maxAllowed = currentBalance < maxBurnPoints() ? currentBalance : maxBurnPoints();

  const effectiveMaxAllowed = maxAllowed;
  const sliderMin = 0;

  const evaluateValidationError = (value) => {
    return "";
  };

  const updateValidationState = (errorMessage) => {
    // setValidationError(errorMessage);
    onValidationStateChange?.(!!errorMessage);
  };

  // Shared calculation — used by both slider and input
  const applyPoints = (value) => {
    const deductedCash = value * burnCalculation.burnRoi;
    const remainingCash = Math.max(0, totalPayableAmount - deductedCash);
    setCash(parseFloat(remainingCash.toFixed(2)));
    onTotalPayableAmountChange(remainingCash);
    setBurnPoints(value);
    setPoints(value);
  };

  // Slider handler
  const handleSliderChange = (event) => {
    const value = Math.max(
      sliderMin,
      Math.min(Number(event.target.value), effectiveMaxAllowed),
    );
    setInputValue(String(value));
    updateValidationState(evaluateValidationError(value));
    applyPoints(value);
  };

  // Input state
  const [inputValue, setInputValue] = useState(String(points));
  const [validationError, setValidationError] = useState("");
  const skipNextBlurCommitRef = useRef(false);

  // Keep input in sync when slider (or external reset) changes points
  useEffect(() => {
    setInputValue(String(points));
    updateValidationState(evaluateValidationError(points));
    if (points === 0) {
      setCash(parseFloat(totalPayableAmount.toFixed(2)));
      onTotalPayableAmountChange(totalPayableAmount);
    }
  }, [points]);

  useLayoutEffect(() => {
    setInputValue(String(sliderMin));
    applyPoints(sliderMin);
  }, [updatedData]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    // Allow only whole digits; block decimals, minus, etc.
    if (/^\d*$/.test(val)) {
      setInputValue(val);
      updateValidationState("");
    }
  };

  const commitInputValue = () => {
    const trimmed = inputValue.trim();
    if (trimmed === "") {
      updateValidationState(
        translation?.invalidPointsValue ?? "Please enter a valid points value.",
      );
      setInputValue(String(points));
      return;
    }

    const parsed = parseInt(trimmed, 10);

    if (isNaN(parsed) || parsed < 0) {
      updateValidationState(
        translation?.negativePointsError ?? "Points cannot be negative.",
      );
      setInputValue(String(points));
      return;
    }

    if (parsed > maxAllowed) {
      updateValidationState(
        `${translation?.maxPointsError ?? "Maximum redeemable points is"} ${maxAllowed.toLocaleString()}.`,
      );
      setInputValue(String(maxAllowed));
      applyPoints(maxAllowed);
      return;
    }

    updateValidationState("");
    applyPoints(parsed);
  };

  const handleInputBlur = () => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false;
      return;
    }
    commitInputValue();
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      commitInputValue();
      skipNextBlurCommitRef.current = true;
      e.target.blur();
    }
  };

  // Conversion rate: 1 Pt = ₹X
  const conversionRateDisplay = (() => {
    if (!burnCalculation.burnRoi) return null;
    return burnCalculation.burnRoi.toFixed(2);
  })();

  const currentPoints = usePoints();

  return (
    <div className={`thepoints mt-3 bnb ${stockOutDenominations ? "disabled-comp" : ""}`}>
      <div className="jlkkedit ddd p-4 pt-2 pb-2 mt-2">
        <div className="lsss">
          {translation?.payWithPointsAndCard} &nbsp;&nbsp;
          <img className="w-144 img" src={img} alt="img" />
        </div>
        <div className="t_aval_points mt-3 mb-2">
          {translation?.totalAvailablePoints}{" "}
          <span>
            {currentPoints?.toLocaleString()} {translation?.pts}
          </span>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center p-3 pt-1">
        <h6 className="thelcpts mx-2 d-flex align-items-center mb-0">
          <span className="pointswithprice">
            <span className="last-cart-pp1 col-2 mr-1">
              {points?.toLocaleString()} {translation?.pts}
            </span>
            <span className="lsss4">&nbsp;+&nbsp;</span>
          </span>
          <span className="lsss3">
            ₹{" "}
            {Number.isInteger(cash)
              ? cash.toLocaleString()
              : cash.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
          </span>
        </h6>
        {/* {conversionRateDisplay && (
          <span className="slider-conversion-rate">
            1 {translation?.pts ?? "Pts"} = ₹ {conversionRateDisplay}
          </span>
        )} */}
      </div>

      <div className="container jlkkedit ddd">
        <div className="d-flex align-items-center">
          <div
            className="d-flex flex-grow-1 align-items-center"
            style={{ maxWidth: "600px", marginRight: "10px" }}
          >
            <fieldset className="form-group bar-poin w-100">
              <div className="rangeSlider redeem-bar d-flex align-items-center w-100">
                <i className="fa fa-circle left-circle1"></i>
                <input
                  type="range"
                  min={sliderMin}
                  max={effectiveMaxAllowed}
                  value={points}
                  className="rsBar flex-grow-1"
                  onChange={handleSliderChange}
                  style={{
                    width: "100%",
                    "--slider-range-percent": `${((points - sliderMin) * 100) / ((effectiveMaxAllowed || 1) - sliderMin)}%`,
                  }}
                />
                <i className="fa fa-circle right-circle1"></i>
              </div>
            </fieldset>
          </div>

          <div className="points-input-wrapper">
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              className={`points-input${validationError ? " points-input--error" : ""}`}
              value={inputValue === "0" ? "" : inputValue}
              placeholder={inputValue === "0" ? "0" : ""}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              disabled={!!stockOutDenominations}
              aria-label={translation?.enterPoints ?? "Enter points"}
            />
            <span className="points-input-suffix">{translation?.pts ?? "Pts"}</span>
          </div>
        </div>

        {validationError && (
          <p className="slider-validation-msg">{validationError}</p>
        )}
      </div>
    </div>
  );
};

export default ThePoints;


